import { EApiCharacterProblemType, IClassroomCharacterData, ICharacterProblem } from '@datatype/iyagigo/api-type';
import { ICharacterQuizChoice, ICharacterQuizQuestion } from '@datatype/iyagigo/type';

// 백엔드 problem 1개 → 화면용 문제
// - 보기 순서는 백엔드 데이터(character.json)를 유지하고, 문제 순서는 프론트에서 유형별로 섞음
// - choice 필드는 의미 고정(ko=한글 발음, ja=가나 표기) — 표시 역할은 문제 type이 결정
//   READING: label=ko(발음 고르기), sublabel=ja / LISTENING: label=ja(글자·단어 고르기), sublabel=ko
const toQuizQuestion = (
  character: IClassroomCharacterData,
  problem: ICharacterProblem,
  problemIndex: number,
): ICharacterQuizQuestion => {
  const isReading = problem.type === EApiCharacterProblemType.READING;
  const choices: ICharacterQuizChoice[] = problem.choice.map((choice, choiceIndex) => ({
    id: `${character.key}-${problemIndex}-${choiceIndex}`,
    label: (isReading ? choice.ko : choice.ja) ?? '',
    sublabel: (isReading ? choice.ja : choice.ko) ?? '',
    isCorrect: choice.correct,
  }));

  return {
    type: problem.type,
    character: character.symbol,
    question: problem.question,
    audioLink: problem.audioLink,
    choices,
  };
};

interface IQuizEntry {
  characterKey: string;
  question: ICharacterQuizQuestion;
}

// Fisher-Yates 셔플. 원본 배열은 변경하지 않는다.
const shuffleQuizEntries = (entries: IQuizEntry[], random: () => number): IQuizEntry[] => {
  const shuffled = [...entries];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }

  return shuffled;
};

// 읽기 → 듣기 순서를 유지하면서, 같은 글자의 두 문제가 바로 이어지지 않도록 듣기 문제를 선택한다.
const interleaveQuizEntries = (readingEntries: IQuizEntry[], listeningEntries: IQuizEntry[]): IQuizEntry[] => {
  const remainingListening = [...listeningEntries];
  const interleaved: IQuizEntry[] = [];

  readingEntries.forEach((readingEntry, index) => {
    interleaved.push(readingEntry);
    if (remainingListening.length === 0) return;

    const nextReadingKey = readingEntries[index + 1]?.characterKey;
    let listeningIndex = remainingListening.findIndex(
      (entry) => entry.characterKey !== readingEntry.characterKey && entry.characterKey !== nextReadingKey,
    );
    if (listeningIndex < 0) {
      listeningIndex = remainingListening.findIndex((entry) => entry.characterKey !== readingEntry.characterKey);
    }
    if (listeningIndex < 0) listeningIndex = 0;

    interleaved.push(remainingListening.splice(listeningIndex, 1)[0]);
  });

  return [...interleaved, ...remainingListening];
};

// 한 그룹(행)의 백엔드 큐레이션 문제를 화면용으로 변환하고, 학습을 시작할 때마다 문제 순서를 섞는다.
export const createGroupQuiz = (
  characters: IClassroomCharacterData[],
  random: () => number = Math.random,
): ICharacterQuizQuestion[] => {
  const entries = characters.flatMap((character) =>
    (character.problem ?? []).map((problem, problemIndex) => ({
      characterKey: character.key,
      question: toQuizQuestion(character, problem, problemIndex),
    })),
  );

  const readingEntries = shuffleQuizEntries(
    entries.filter((entry) => entry.question.type === EApiCharacterProblemType.READING),
    random,
  );
  const listeningEntries = shuffleQuizEntries(
    entries.filter((entry) => entry.question.type === EApiCharacterProblemType.LISTENING),
    random,
  );

  return interleaveQuizEntries(readingEntries, listeningEntries).map((entry) => entry.question);
};
